import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAdmin, RequireAuth } from './auth/RequireAuth';
import { CustomerNav } from './customer/CustomerNav';
import { CustomerSessionProvider } from './customer/CustomerSession';
import { GarmentsScreen } from './customer/GarmentsScreen';
import { CaptureScreen } from './customer/CaptureScreen';
import { TryOnScreen } from './customer/TryOnScreen';
import { Spinner } from './ui/Spinner';

/**
 * The admin dashboard is code-split. It pulls in dnd-kit for reorder mode, and
 * none of that should ship to a shop device that only ever runs the customer
 * flow on wifi it does not control.
 */
const AdminLayout = lazy(() =>
  import('./admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const GarmentList = lazy(() =>
  import('./admin/GarmentList').then((m) => ({ default: m.GarmentList })),
);
const GarmentEdit = lazy(() =>
  import('./admin/GarmentEdit').then((m) => ({ default: m.GarmentEdit })),
);
const ReorderMode = lazy(() =>
  import('./admin/ReorderMode').then((m) => ({ default: m.ReorderMode })),
);
const DeviceList = lazy(() =>
  import('./admin/DeviceList').then((m) => ({ default: m.DeviceList })),
);
const QuotaView = lazy(() => import('./admin/QuotaView').then((m) => ({ default: m.QuotaView })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * A shop device sits idle for long stretches and then gets picked up. A
       * refetch on focus is exactly right for the catalogue, and cheap.
       */
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Never retry a mutation automatically — several of these cost money or
      // change a credential.
      retry: 0,
    },
  },
});

function AdminFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas text-tertiary">
      <Spinner label="Loading dashboard" />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <RequireAuth>
            {/*
              ⚠️ Wraps **both** route trees, admin included. `CustomerNav` reads
              the visit to decide whether leaving needs a warning, and the nav is
              rendered by the admin shell too — so a provider scoped to the
              customer routes would throw the moment /admin rendered it.

              Costs nothing on the admin side: the provider is two
              `sessionStorage` reads and holds no subscription.
            */}
            <CustomerSessionProvider>
              <Suspense fallback={<AdminFallback />}>
                <Routes>
                  {/*
                    Customer flow. Everyone lands on Garments, admin included.

                    ⚠️ Garments is browse-only and `/try-on` is gated on a photo
                    — the nav routes through `/capture` to get one. Nothing on
                    the garments screen starts a generation.

                    ⚠️ **No `CustomerSessionProvider` here.** There used to be
                    one, and once the nav moved onto the admin shell it became a
                    second, independent copy of the visit: the customer screens
                    would read the inner one and the admin nav the outer, so
                    ending a visit in one left the other still believing it was
                    live. One provider, above both trees.
                  */}
                  <Route element={<CustomerRoutes />}>
                    <Route path="/" element={<GarmentsScreen />} />
                    <Route path="/capture" element={<CaptureScreen />} />
                    <Route path="/try-on" element={<TryOnScreen />} />
                  </Route>

                  <Route
                    path="/admin"
                    element={
                      <RequireAdmin>
                        <AdminLayout />
                      </RequireAdmin>
                    }
                  >
                    <Route index element={<GarmentList />} />
                    <Route path="garments/:id" element={<GarmentEdit />} />
                    <Route path="reorder" element={<ReorderMode />} />
                    <Route path="devices" element={<DeviceList />} />
                    <Route path="quota" element={<QuotaView />} />
                  </Route>

                  {/*
                    A redirect rather than a screen: rendered here it would sit
                    outside the customer layout route, so it would come up with
                    no nav and none of the viewport box that route owns.
                  */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </CustomerSessionProvider>
          </RequireAuth>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  );
}

/**
 * Layout route for the customer surface — exists so the three customer screens
 * share one `CustomerSessionProvider` and therefore one photo key, and now so
 * they share one nav bar.
 *
 * ⚠️ This element owns the viewport box (`min-h-dvh`, canvas background) that
 * each screen used to own for itself. The screens are `flex-1` children of it,
 * which is what keeps their existing `sticky bottom-0` bars working: nothing
 * here introduces an `overflow` container, so sticky still resolves against the
 * viewport rather than against a new scrollport.
 */
function CustomerRoutes() {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <CustomerNav />
      <Outlet />
    </div>
  );
}
